import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { takeUntil } from 'rxjs';
import { CarrierInformation } from 'src/app/core/models/carrier-information.model';
import { InviteSignupService } from 'src/components/invite-signup/invite-signup.service';
import { EnvService } from 'src/services/env.service';
import { HelpService } from 'src/services/help.service';
import { NgClass } from '@angular/common';
import { BrandInformationComponent } from '../../brand-information/brand-information.component';
import { CxHiddenLoginFormComponent, CxLoginCredentials } from 'src/components/cx-hidden-login-form/cx-hidden-login-form.component';

@Component({
    selector: 'app-agency-login',
    templateUrl: './agency-login.component.html',
    styleUrls: ['./agency-login.component.less'],
    imports: [
        WaitComponent,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        MessageBoxComponent,
        InputComponent,
        AutoFocusDirective,
        WaitButtonComponent,
        BrandInformationComponent,
        CxHiddenLoginFormComponent
    ]
})
export class AgencyLoginComponent extends BaseComponent implements OnInit {

    readonly SUBSCRIBER_COOKIE: string = 'rememberMe';

    submitSignal: WritableSignal<CxLoginCredentials | null> = signal(null);

    errors: MessageContent = ErrorsUtil.create();
    loadingBrand = true;
    submitting = false;

    brandKey = null;
    redirectRoute = null;
    redirectParams: string[] = [];
    documentPartyIds: string[] = [];

    carrierInformation: CarrierInformation;
    sirconUrl: string;

    formData: FormGroup;
    cookieInUse: boolean;
    confirmingSso = false;

    // Angular Router properties
    private subscriberId: string;

    constructor(
        private env: EnvService,
        private helpService: HelpService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private cookieService: CookieService,
        private inviteSignupService: InviteSignupService
    ) {
        super();
        this.formData = this.formBuilder.group({
            accountId: ['', [Validators.required, Validators.maxLength(9)]],
            loginName: ['', Validators.required],
            password: ['', Validators.required]
        });
    }

    ngOnInit(): void {
        this.sirconUrl = this.env.SIRCON_URL;

        this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
            this.brandKey = params.get('brandKey') || null;
            this.redirectRoute = params.get('redirectRoute') || null;

            const redirectParamValues = params.getAll('redirectParam');
            // redirectParam may arrive either as repeated keys (?redirectParam=a=1&redirectParam=b=2)
            // or as a single comma-separated value. getAll() captures every occurrence; splitting each
            // on comma covers both cases (preserves FOF-5501 compatibility).
            this.redirectParams = redirectParamValues.flatMap(value => value.split(','));
            this.applyLegacyReconstruction();

            this.subscriberId = params.get('subscriberId');
            this.formData.patchValue({
                loginName: params.get('username') || '',
                accountId: this.subscriberId || ''
            });

            // Handle errors parameter - might be JSON string
            const errorsParam = params.get('errors');
            if (errorsParam) {
                try {
                    this.errors = JSON.parse(errorsParam);
                } catch {
                    this.errors = ErrorsUtil.create();
                }
            }

            this.loadingBrand = !!this.brandKey;
        });
        // We don't want to wipe out the cookie in use on the main website even though we don't directly support it
        this.cookieInUse = !!(this.cookieService.get(this.SUBSCRIBER_COOKIE) && this.cookieService.get(this.SUBSCRIBER_COOKIE) !== '');
        this.confirmingSso = this.cookieService.check(this.env.storageKeys.PRODUCER_SAML_LOGIN);
        if (this.confirmingSso && !this.redirectRoute) {
            this.redirectRoute = 'ssoLogin';
            this.redirectParams = [
                `samlConfirmationId=${this.getAndDeleteCookie(this.env.storageKeys.PRODUCER_SAML_LOGIN)}`,
                `subscriberId=${this.getAndDeleteCookie(this.env.storageKeys.SUBSCRIBER_ID)}`
            ];
        }
    }

    /**
     * Fix broken JSON arrays in documentPartyId parameter.
     *
     * When isMultipleDocuments=true, documentPartyId contains a JSON array like ["id1","id2"].
     * The comma-splitting in getAll().flatMap(value => value.split(',')) breaks this array
     * into multiple parameters. This method reconstructs the full JSON array.
     */
    private applyLegacyReconstruction(): void {
        const isMultipleDocuments = this.redirectParams.some(param => param === 'isMultipleDocuments=true');
        if (!isMultipleDocuments) {
            return;
        }

        const documentPartyIdIndex = this.redirectParams.findIndex(param => param.startsWith('documentPartyId'));
        if (documentPartyIdIndex === -1) {
            return;
        }

        try {
            const reconstructedValue = this.redirectParams[documentPartyIdIndex];
            const valueParts = reconstructedValue.split('=').slice(1);
            let fullValue = valueParts.join('=');

            let decodedValue = decodeURIComponent(fullValue);
            if (!decodedValue.trim().startsWith('[')) {
                // When isMultipleDocuments=true, we expect a JSON array. Anything else is malformed.
                console.error('applyLegacyReconstruction: Expected JSON array for documentPartyId, got:', decodedValue);
                this.errors = ErrorsUtil.create();
                this.errors.unexpectedError = true;
                return;
            }

            let currentIndex = documentPartyIdIndex + 1;
            while (currentIndex < this.redirectParams.length && !decodedValue.trim().endsWith(']')) {
                const nextParam = this.redirectParams[currentIndex];
                fullValue += ',' + nextParam;
                decodedValue = decodeURIComponent(fullValue);
                currentIndex++;
            }

            const documentPartyIdArray = JSON.parse(decodedValue);

            if (!Array.isArray(documentPartyIdArray) || documentPartyIdArray.length === 0) {
                console.warn('applyLegacyReconstruction: documentPartyId is not a valid array', decodedValue);
                return;
            }

            const documentPartyIdJsonString = JSON.stringify(documentPartyIdArray);
            const urlEncodedDocumentPartyId = encodeURIComponent(documentPartyIdJsonString);

            const partsToRemove = currentIndex - documentPartyIdIndex;
            this.redirectParams.splice(documentPartyIdIndex, partsToRemove, `documentPartyId=${urlEncodedDocumentPartyId}`);
        } catch (error) {
            console.error('applyLegacyReconstruction: Failed to reconstruct documentPartyId parameter', error);
            this.errors = ErrorsUtil.create();
            this.errors.unexpectedError = true;
        }
    }

    authenticate(): void {
        if (this.formData.valid) {
            this.submitting = true;
            this.submitHiddenForm();
        }
    }

    /**
     * Submits the hidden CX login form with current form values.
     * Triggers submission reactively by updating the submitSignal.
     */
    submitHiddenForm(): void {
        this.submitSignal.set({
            accountId: this.formData.value.accountId,
            loginName: this.formData.value.loginName,
            password: this.formData.value.password
        });
    }

    /**
     * Handles form submission errors from the hidden login form.
     * Resets submitting state and displays error message to user.
     */
    handleSubmissionError(): void {
        this.submitting = false;
        this.errors.unexpectedError = true;
    }

    brandedWalkupHelpUrl(): string {
        return this.helpService.getInsuranceHelpPath();
    }

    loadingBrandEvent(loading: boolean): void {
        this.loadingBrand = loading;
    }

    carrierInformationEvent(carrierInformation: CarrierInformation): void {
        this.carrierInformation = carrierInformation;
        // Branded login used to be used by carriers from portals.  Now it is also used
        // by emails including after an invite signup.  So do not go to the carrier profile
        // when coming from an invite since a "to-do" might be a result.  A redirect in the
        // email will be honored.
        const overrideRedirect = !this.redirectRoute && this.redirectParams.length === 0;
        if (overrideRedirect && !this.inviteSignupService.getInvite()) {
            const defaultCarrierBrandedRedirectRoute = 'network.carrierAgencyRedirect';
            const defaultCarrierBrandedRedirectParams = [
                `carrierNetworkId=${carrierInformation.carrierNetworkId}`,
                `carrierName=${carrierInformation.carrierName}`
            ];

            this.redirectRoute = defaultCarrierBrandedRedirectRoute;
            this.redirectParams = defaultCarrierBrandedRedirectParams;
        }
    }

    goToSignInHelpWorkflow(): void {
        this.router.navigate(['/signInHelpWorkflow']);
    }

    getAndDeleteCookie(name: string) {
        const value = this.cookieService.get(name);
        this.cookieService.delete(
            name,
            this.env.COOKIES_OPTIONS.path,
            this.env.COOKIES_OPTIONS.domain,
            this.env.COOKIES_OPTIONS.secure,
            this.env.COOKIES_OPTIONS.sameSite);
        return value;
    }

    freezeSubscriberId(): boolean {
        return this.confirmingSso && !!this.subscriberId;
    }
}
